/**
 * @desc 获取需要显示的列表
 * @param list
 * @returns {array}
 */
import {
  FIELD_TYPE_CHECKBOX,
  FIELD_TYPE_DATE,
  FIELD_TYPE_DATERANGE,
  FIELD_TYPE_DATETIME, FIELD_TYPE_DATETIME_RANGE,
  FIELD_TYPE_INPUT, FIELD_TYPE_MULTIPLE_SELECT, FIELD_TYPE_POP_MULTI,
  FIELD_TYPE_POP_ONE,
  FIELD_TYPE_RADIO,
  FIELD_TYPE_SELECT,
  FIELD_TYPE_TEXTAREA
} from './x-condition-constant';
import {isEmpty, isString} from "@/util/appUtils";

/**
 * 获取需要显示list
 * @param list
 * @param config
 * @param parentConfig
 * @return {*[]}
 */
export function getShowList({
  list,
  config,
  parentConfig
}) {
  const arr = [];
  if (!Array.isArray(list)) {
    return arr;
  }
  for (let i = 0; i < list.length; i++) {
    let item = list[i];
    // 有children的，递归处理
    if (Array.isArray(item.children)) {
      const children = getShowList({
        list: item.children,
        config,
        parentConfig
      });
      if (children.length > 0) {
        // 这里需要做一次拷贝，因为不能修改原数据的非叶子节点
        item = Object.assign({}, item);
        item.children = children;
        arr.push(item);
      }
      continue;
    }
    // 如果传入的是boolean，且为false
    if ((typeof item.show).toLowerCase() === 'boolean' && !item.show) {
      continue;
    }
    // 如果传入的是函数
    if (item.show instanceof Function && !item.show({
      item,
      config,
      parentConfig
    })) {
      continue;
    }
    arr.push(item);
  }
  return arr;
}

/**
 * 根据field获取对应的条件
 * @param list
 * @param field
 * @return {null|any}
 */
export function getConditionItem({
  list,
  field
}) {
  return getListItem({
    list,
    callback: (item) => {
      return item.field === field;
    }
  });
}

/**
 * 在columns中获取对应的元素
 * @param columns
 * @param field
 */
export function getColumnItem({
  columns,
  field
}) {
  return getListItem({
    list: columns,
    callback: (item) => {
      return item.field === field;
    }
  });
}

/**
 * 获取item的公共方法
 * @param list
 * @param childrenProperty 子元素数组的属性名
 * @param callback
 * @return {null|*}
 */
export function getListItem({
  list,
  childrenProperty = 'children',
  callback
}) {
  function getListItemRecursive({
    list,
    childrenProperty = 'children',
    callback
  }) {
    if (!Array.isArray(list)) {
      return null;
    }
    for (const item of list) {
      if (callback(item)) {
        return item;
      }
      const child = getListItemRecursive({
        list: item[childrenProperty],
        childrenProperty,
        callback
      });
      if (child) {
        return child;
      }
    }
    return null;
  }

  return getListItemRecursive({
    list,
    childrenProperty,
    callback
  });
}

/**
 * 根据list获取对应的model
 * @param list 查询条件列表
 * @param keyProperty list中每个元素的唯一标识
 * @param valueProperty list中每个元素的预设值
 * @return {object} 根据list中每个元素的field和value组装的对象
 */
export function getConditionModel({
  list = [],
  keyProperty = 'field',
  valueProperty = 'value'
}) {
  const obj = {};
  for (const item of list) {
    // 部分字段只是用于页面显示，不需要接口传参
    if (item.toQuery === false) {
      continue;
    }
    obj[item[keyProperty]] = item[valueProperty];
  }
  return obj;
}

/**
 * @desc 在conditionConfig查找slot项并返回
 * @param conditionConfig
 * @return {array}
 */
export function getConditionSlotArray(conditionConfig) {
  // 这里只是作为中间插槽暴露，所以将所有的插槽都直接暴露出去
  const arr = [];
  if (!conditionConfig || !Array.isArray(conditionConfig.list)) {
    return arr;
  }
  for (const item of conditionConfig.list) {
    if (item.slot) {
      arr.push(item.slot);
    }
    if (item.suffixSlot) {
      arr.push(item.suffixSlot);
    }
    if (item.errorSlot) {
      arr.push(item.errorSlot);
    }
  }
  return arr;
}

/**
 * 树拷贝
 * @param source
 * @param childrenProperty
 * @param validate 校验元素是否需要拷贝的函数
 * @param copyMethod 自定义复制方法，如果传入了copyMethod，propertyArray就变为无效
 * @param propertyArray 需要拷贝的属性
 * @return {array}
 */
export function copyTree({
  source,
  childrenProperty = 'children',
  validate,
  copyMethod,
  propertyArray
}) {
  if (!Array.isArray(source)) {
    throw new Error('source must be array');
  }
  const target = [];

  function copyTreeRecursive({
    source,
    target,
    childrenProperty,
    validate,
    copyMethod,
    propertyArray
  }) {
    for (const item of source) {
      if (validate && !validate({
        source,
        item
      })) {
        continue;
      }
      if (copyMethod) {
        // 使用自定义复制方法
        target.push(copyMethod({item}));
      } else if (Array.isArray(propertyArray)) {
        // 根据属性数组进行复制
        const newItem = {};
        for (const property of propertyArray) {
          newItem[property] = item[property];
        }
        target.push(newItem);
      } else {
        // 全量复制
        target.push(Object.assign({}, item));
      }
      if (Array.isArray(item[childrenProperty])) {
        const newItem = target[target.length - 1];
        newItem[childrenProperty] = [];
        copyTreeRecursive({
          source: item[childrenProperty],
          target: newItem[childrenProperty],
          childrenProperty,
          validate,
          copyMethod,
          propertyArray
        });
      }
    }
  }

  copyTreeRecursive({
    source,
    target,
    childrenProperty,
    validate,
    copyMethod,
    propertyArray
  });
  return target;
}

/**
 * 对比两棵树是否一样
 * @param reference
 * @param target
 * @param childrenProperty
 * @param validate
 * @return {boolean}
 */
export function compareTree({
  reference,
  target,
  childrenProperty = 'children',
  validate
}) {
  let flag = true;

  function compareTreeRecursive({
    reference,
    target,
    childrenProperty,
    validate
  }) {
    const flag1 = Array.isArray(reference);
    const flag2 = Array.isArray(target);
    // 先排除两个都是空的情况
    if (!flag1 && !flag2) {
      return;
    }
    if (flag1 && flag2) {
      for (const referenceItem of reference) {
        let i;
        for (i = 0; i < target.length; i++) {
          const targetItem = target[i];
          if (validate({
            targetItem,
            referenceItem
          })) {
            compareTreeRecursive({
              reference: referenceItem[childrenProperty],
              target: targetItem[childrenProperty],
              childrenProperty,
              validate
            });
            break;
          }
        }
        if (i === target.length) {
          flag = false;
          return;
        }
      }
      return;
    }
    flag = false;
  }

  compareTreeRecursive({
    reference,
    target,
    childrenProperty,
    validate
  });
  compareTreeRecursive({
    reference: target,
    target: reference,
    childrenProperty,
    validate
  });
  return flag;
}

/**
 * 获取所有的叶子节点
 * @param tree
 * @param childrenProperty
 * @return {array}
 */
export function getLeafList({
  tree,
  childrenProperty = 'children'
}) {
  const list = [];

  function getLeafListRecursive({
    tree,
    list,
    childrenProperty
  }) {
    if (!Array.isArray(tree)) {
      return;
    }
    for (const item of tree) {
      // 如果是叶子节点，就放入list中
      if (!Object.prototype.hasOwnProperty.call(item, childrenProperty)) {
        list.push(item);
        continue;
      }
      // 如果还有children，就递归到下一层
      getLeafListRecursive({
        tree: item[childrenProperty],
        list,
        childrenProperty
      });
    }
  }

  getLeafListRecursive({
    tree,
    list,
    childrenProperty
  });
  return list;
}

/**
 * @desc 清空model
 * @param conditionConfig
 */
export function clearConditionConfigModel({conditionConfig}) {
  const model = conditionConfig.model;
  const list = conditionConfig.list;
  for (const item of list) {
    const field = item.field;
    switch (item.type) {
      case FIELD_TYPE_INPUT:
      case FIELD_TYPE_SELECT:
      case FIELD_TYPE_TEXTAREA:
      case FIELD_TYPE_RADIO:
        model[field] = '';
        break;
      case FIELD_TYPE_CHECKBOX:
        if (Object.prototype.hasOwnProperty.call(item, 'falseLabel')) {
          model[field] = item.falseLabel;
        } else {
          model[field] = false;
        }
        break;
      case FIELD_TYPE_POP_ONE:
      case FIELD_TYPE_DATE:
      case FIELD_TYPE_DATETIME:
        model[field] = null;
        break;
      case FIELD_TYPE_DATERANGE:
      case FIELD_TYPE_DATETIME_RANGE:
      case FIELD_TYPE_POP_MULTI:
      case FIELD_TYPE_MULTIPLE_SELECT:
        model[field] = [];
        break;
      default:
        break;
    }
    if (Object.prototype.hasOwnProperty.call(item, 'value')) {
      model[field] = item.value;
    }
  }
}

/**
 * 数组转map
 * @param array
 * @param keyProperty
 * @return {object}
 */
export function arrayToMap({
  array,
  keyProperty
}) {
  const map = {};
  for (const item of array) {
    map[item[keyProperty]] = item;
  }
  return map;
}

/**
 * @desc 使用setTimeout的方式实现倒计时
 * @param handler 回调函数
 * @param timeLimit 倒计时次数 默认值10
 * @param delay 倒计时每两次回调之间的时间间隔，单位毫秒， 默认1000毫秒
 */
export function countDown({
  handler,
  timeLimit = 10,
  delay = 1000
}) {
  handler({time: timeLimit});
  if (timeLimit > 0) {
    timeLimit--;
    setTimeout(() => {
      countDown({
        handler,
        timeLimit
      });
    }, delay);
  }
}

/**
 * 属性复制
 * @param source
 * @param target
 * @param key 属性名
 * @param defaultValue
 */
export function copyProperty({
  source,
  target,
  key,
  defaultValue
}) {
  if (Object.prototype.hasOwnProperty.call(source, key)) {
    target[key] = source[key];
    return;
  }
  if (defaultValue !== undefined) {
    target[key] = defaultValue;
  }
}

/**
 * 添加css class
 * @param collection
 * @param item
 * @returns {*|string}
 */
export const addClass = ({collection, item}) => {
  if (isEmpty(item)) {
    return collection;
  }
  if (isEmpty(collection)) {
    return item;
  }
  // 字符串的形式
  if (isString(collection)) {
    const arr = collection.split(/\s+/);
    for (const $item of arr) {
      if ($item === item) {
        return collection;
      }
    }
    return collection + ' ' + item;
  }
  // 数组形式
  if (Array.isArray(collection)) {
    for (const item of collection) {
      if (item === item) {
        collection.push(item);
        return collection;
      }
    }
  }
  // 简单对象形式
  if (!collection[item]) {
    collection[item] = true;
  }
  return collection;
};
